import { isAxiosError } from 'axios';

// 헷갈리기 쉬운 문자(0/O, 1/l/I)는 뺀다 — 파트너에게 메신저로 전달할 때 오독 방지
const LOWER = 'abcdefghijkmnopqrstuvwxyz';
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const DIGIT = '23456789';
const SYMBOL = '!@#$%^&*';
const ALL = LOWER + UPPER + DIGIT + SYMBOL;

const randomIndex = (max: number) => {
  // 모듈로 편향을 없애기 위해 범위를 벗어난 값은 버리고 다시 뽑는다
  const limit = Math.floor(0x100000000 / max) * max;
  const buffer = new Uint32Array(1);

  do {
    window.crypto.getRandomValues(buffer);
  } while (buffer[0] >= limit);

  return buffer[0] % max;
};

const pick = (chars: string) => chars[randomIndex(chars.length)];

/** 소문자 · 대문자 · 숫자 · 특수문자를 최소 1자씩 포함하는 임시 비밀번호 */
export const generatePassword = (length = 12) => {
  const chars = [pick(LOWER), pick(UPPER), pick(DIGIT), pick(SYMBOL)];

  while (chars.length < length) {
    chars.push(pick(ALL));
  }

  // Fisher-Yates 셔플: 필수 문자 4개가 항상 앞에 오지 않도록
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }

  return chars.join('');
};

/** navigator.clipboard 가 없는 환경(비보안 컨텍스트 등)은 textarea 로 대체 */
export const copyToClipboard = async (text: string) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();

  try {
    if (!document.execCommand('copy')) {
      throw new Error('copy failed');
    }
  } finally {
    document.body.removeChild(textarea);
  }
};

/**
 * 서버 에러 응답에서 사용자에게 보여줄 문구를 꺼낸다.
 * - 커스텀 예외: `{ error, description }`
 * - DRF 검증 에러: `{ field: ["메시지"] }`
 */
export const getApiErrorMessages = (
  error: unknown
): { message: string; fields: Record<string, string> } => {
  const fallback = '요청을 처리하지 못했어요. 잠시 후 다시 시도해주세요.';

  if (!isAxiosError(error) || !error.response?.data) {
    return { message: fallback, fields: {} };
  }

  const data = error.response.data as Record<string, unknown>;

  if (typeof data.description === 'string') {
    return { message: data.description, fields: {} };
  }

  const fields = Object.entries(data).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      const text = Array.isArray(value) ? value[0] : value;

      if (typeof text === 'string') {
        acc[key] = text;
      }

      return acc;
    },
    {}
  );
  const first = Object.values(fields)[0];

  return { message: first ?? fallback, fields };
};
